/**
 * QuickActions - Recreation of Base44 QuickActions Component
 * Rapid operation buttons for common cloud management tasks
 * 
 * @description Professional quick actions interface with provider-specific
 * theming, loading states, and contextual action availability
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Rocket, 
  DollarSign, 
  Download,
  Scan
} from 'lucide-react';

// Quick actions configuration matching Base44 patterns
const quickActions = [
  {
    id: 'deploy',
    title: 'Deploy Resource',
    description: 'Launch new instances or services',
    icon: Rocket,
    border: 'bg-blue-500',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600'
  },
  {
    id: 'backup',
    title: 'Create Backup',
    description: 'Backup critical resources',
    icon: Download,
    border: 'bg-green-500',
    bgColor: 'bg-green-50',
    textColor: 'text-green-600'
  },
  {
    id: 'costs',
    title: 'View Costs',
    description: 'Analyze spending patterns',
    icon: DollarSign,
    border: 'bg-purple-500',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-600'
  },
  {
    id: 'security',
    title: 'Security Scan',
    description: 'Run compliance checks',
    icon: Scan,
    border: 'bg-red-500',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600'
  }
];

// TypeScript interfaces for type safety
interface QuickActionsProps {
  activeProvider: string;
  onActionClick?: (actionId: string) => void;
}

export function QuickActions({ activeProvider, onActionClick }: QuickActionsProps) {
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  // Handle action clicks with loading state
  const handleAction = async (actionId: string) => {
    setLoadingAction(actionId);
    
    // Call parent handler if provided
    if (onActionClick) {
      onActionClick(actionId);
    }
    
    // Simulate action loading (remove in real implementation)
    setTimeout(() => {
      setLoadingAction(null);
    }, 2000);
  };

  // Get provider-specific border color
  const getProviderColor = (): string => {
    switch (activeProvider) {
      case 'aws': return 'border-orange-200';
      case 'gcp': return 'border-blue-200';
      case 'azure': return 'border-cyan-200';
      default: return 'border-border';
    }
  };

  return (
    <Card className={`border-2 ${getProviderColor()}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
          <Badge variant="outline" className="text-xs">
            {activeProvider.toUpperCase()}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quickActions.map((action) => (
            <div
              key={action.id}
              className={`p-4 rounded-lg ${action.bgColor} border border-opacity-20 hover:shadow-md transition-all duration-200 cursor-pointer group !bg-[var(--background)] !text-[var(--card-foreground)]`}
              onClick={() => handleAction(action.id)}
            >
              <div className="flex items-start gap-3 ">
                {/* Action icon */}
                <div className={`p-2 rounded-lg border-2 border-[${action.border}] bg-opacity-10 group-hover:bg-opacity-20 transition-all`}>
                  <action.icon className={`w-5 h-5 ${action.textColor}`} />
                </div>
                
                {/* Action content */}
                <div className="flex-1">
                  <h3 className={`font-medium ${action.textColor} mb-1`}>{action.title}</h3>
                  <p className="text-sm text-muted-foreground">{action.description}</p>
                  
                  {/* Loading indicator */}
                  {loadingAction === action.id && (
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-3 h-3 border-2 border-current border-r-transparent rounded-full animate-spin" />
                      <span className="text-xs text-muted-foreground">Processing...</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default QuickActions;